import styles from './transactionTableCard.module.scss';
import Selectors from 'src/utils/selectors';
import React from 'react';
import { TransactionModel } from 'src/types/transaction.model';
import TransactionNameAccount from 'src/components/atomic/atoms/transactionNameAccount/TransactionNameAccount';
import TransactionAmount from 'src/components/atomic/atoms/transactionAmount/TransactionAmount';
import TransactionTypeText from 'src/components/atomic/atoms/transactionTypeText/TransactionTypeText';
import TransactionCategoryCell from 'src/components/atomic/molecules/transactionCategoryCell/TransactionCategoryCell';
import EditTransactionButton from 'src/components/atomic/atoms/editTransactionButton/EditTransactionButton';
import Divider from 'src/components/atomic/atoms/divider/Divider';
import TransactionDate from 'src/components/atomic/atoms/transactionDate/TransactionDate';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { useUpdatingTransaction } from 'src/hooks/useUpdatingTransaction';
import TransactionTypeSkeletonLoader from 'src/components/atomic/atoms/transactionTypeSkeletonLoader/TransactionTypeSkeletonLoader';
import { Formik } from 'formik';
import AutoSaveTransaction from 'src/components/auto-save/AutoSaveTransaction';
import { WalkmeName, WalkmePopupWrapper } from '../walkme';
import { CategoryModel } from 'src/types/category.model';
import RulePromptWrapper, { RulePromptState } from 'src/components/atomic/molecules/rulePrompt/RulePrompt';
import { useUserAttributes } from 'src/hooks/queries/users';
import oneXp from 'src/types/one-xp';
import { useFeatureFlag } from 'src/hooks/queries/one-xp';
import { shouldShowRulePromptForTransactionTable } from 'src/utils/transactions.util';

interface TransactionTableCardFeatureWrapperProps {
    transaction: TransactionModel;
    categories: CategoryModel[];
    rowIndex: number;
    rulePromptState?: RulePromptState;
    setRulePromptState?: (state: RulePromptState) => void;
    displayingTransactionRulePrompt?: TransactionModel;
    setDisplayingTransactionRulePrompt?: (transaction: TransactionModel) => void;
}

export default function TransactionTableCardFeatureWrapper({
    transaction,
    categories,
    rowIndex,
    rulePromptState,
    setRulePromptState,
    displayingTransactionRulePrompt,
    setDisplayingTransactionRulePrompt
}: TransactionTableCardFeatureWrapperProps): JSX.Element {
    const { data: transactionsContextualRulesFeatureFlag } = useFeatureFlag(
        oneXp.FeatureFlags.TransactionsContextualRules
    );

    return (
        <TransactionTableCardDataWrapper
            transaction={transaction}
            categories={categories}
            rowIndex={rowIndex}
            rulePromptState={rulePromptState}
            setRulePromptState={setRulePromptState}
            displayingTransactionRulePrompt={displayingTransactionRulePrompt}
            setDisplayingTransactionRulePrompt={setDisplayingTransactionRulePrompt}
            featureFlagOn={transactionsContextualRulesFeatureFlag}
        />
    );
}

export interface TransactionTableCardDataWrapperProps {
    transaction: TransactionModel;
    categories: CategoryModel[];
    rowIndex: number;
    rulePromptState?: RulePromptState;
    setRulePromptState?: (state: RulePromptState) => void;
    displayingTransactionRulePrompt?: TransactionModel;
    setDisplayingTransactionRulePrompt?: (transaction: TransactionModel) => void;
    featureFlagOn?: boolean;
}

export function TransactionTableCardDataWrapper({
    transaction,
    categories,
    rowIndex,
    rulePromptState,
    setRulePromptState,
    displayingTransactionRulePrompt,
    setDisplayingTransactionRulePrompt,
    featureFlagOn
}: TransactionTableCardDataWrapperProps): JSX.Element {
    const { isMinWidthSmall, isMinWidthMedium } = useBreakpoints();
    const { isUpdatingTransaction } = TransactionTableCard.useUpdatingTransaction(transaction.id, true);
    const { data: userAttributes } = useUserAttributes();
    const showTransactionRulePrompt =
        featureFlagOn &&
        isMinWidthMedium &&
        shouldShowRulePromptForTransactionTable(
            displayingTransactionRulePrompt?.id,
            transaction?.id,
            userAttributes,
            rulePromptState
        );
    const isFirstRow: boolean = rowIndex === 0;

    const handleCategoryChange = (groupUid: string): void => {
        if (setDisplayingTransactionRulePrompt) setDisplayingTransactionRulePrompt({ ...transaction, groupUid });
    };
    const handleCloseRulePrompt = (): void => {
        if (setDisplayingTransactionRulePrompt) setDisplayingTransactionRulePrompt(undefined);
    };

    return (
        <TransactionTableCard
            transaction={transaction}
            categories={categories}
            rowIndex={rowIndex}
            isMinWidthSmall={isMinWidthSmall}
            isMinWidthMedium={isMinWidthMedium}
            isFirstRow={isFirstRow}
            displayingTransactionRulePrompt={displayingTransactionRulePrompt}
            handleCategoryChange={handleCategoryChange}
            handleCloseRulePrompt={handleCloseRulePrompt}
            rulePromptState={rulePromptState}
            setRulePromptState={setRulePromptState}
            showTransactionRulePrompt={showTransactionRulePrompt}
            isUpdatingTransaction={isUpdatingTransaction}
        />
    );
}

export interface TransactionTableCardProps {
    transaction: TransactionModel;
    categories: CategoryModel[];
    rowIndex: number;
    isMinWidthSmall?: boolean;
    isMinWidthMedium?: boolean;
    isFirstRow?: boolean;
    showTransactionRulePrompt?: boolean;
    displayingTransactionRulePrompt?: TransactionModel;
    handleCategoryChange?: (groupUid: string) => void;
    handleCloseRulePrompt?: () => void;
    rulePromptState?: RulePromptState;
    setRulePromptState?: (state: RulePromptState) => void;
    isUpdatingTransaction?: boolean;
}

export function TransactionTableCard({
    transaction,
    categories,
    rowIndex,
    isMinWidthSmall,
    isMinWidthMedium,
    isFirstRow,
    showTransactionRulePrompt,
    displayingTransactionRulePrompt,
    handleCategoryChange,
    handleCloseRulePrompt,
    rulePromptState,
    setRulePromptState,
    isUpdatingTransaction
}: TransactionTableCardProps): JSX.Element {
    return (
        <Formik initialValues={{ transaction }} enableReinitialize onSubmit={() => {}}>
            <>
                <div
                    id={`transactionTableCard-${rowIndex}-${transaction.id}`}
                    className={styles.cardRow}
                    data-cy={Selectors.Transactions.CARD_ROW}
                >
                    <div className={styles.dateAndName}>
                        <TransactionDate date={transaction.date} />
                        {isFirstRow && isMinWidthSmall && !isMinWidthMedium ? (
                            <WalkmePopupWrapper
                                walkmeName={WalkmeName.TRANSACTION_CLASSIFICATION}
                                stepIndex={4}
                                overrides={{ left: '-44%' }}
                            >
                                <TransactionNameAccount
                                    transactionId={transaction.id}
                                    accountId={transaction.accountId}
                                    transactionDisplayName={transaction.displayName}
                                    transactionDate={transaction.date}
                                    merchantName={transaction.merchantName}
                                />
                            </WalkmePopupWrapper>
                        ) : (
                            <TransactionNameAccount
                                transactionId={transaction.id}
                                accountId={transaction.accountId}
                                transactionDisplayName={transaction.displayName}
                                transactionDate={transaction.date}
                                merchantName={transaction.merchantName}
                            />
                        )}
                    </div>
                    <TransactionAmount amount={transaction.amount} />
                    {isMinWidthSmall && (
                        <>
                            {isFirstRow ? (
                                <WalkmePopupWrapper walkmeName={WalkmeName.TRANSACTION_CLASSIFICATION} stepIndex={3}>
                                    {isUpdatingTransaction ? (
                                        <TransactionTypeSkeletonLoader />
                                    ) : (
                                        <TransactionTypeText transactionType={transaction.type} />
                                    )}
                                </WalkmePopupWrapper>
                            ) : isUpdatingTransaction ? (
                                <TransactionTypeSkeletonLoader />
                            ) : (
                                <TransactionTypeText transactionType={transaction.type} />
                            )}
                            <div className={styles.dropdownAndButton}>
                                {isMinWidthMedium ? (
                                    !isFirstRow ? (
                                        <TransactionCategoryCell
                                            categories={categories}
                                            baseFieldId="transaction"
                                            transaction={transaction}
                                            className={styles.dropdown}
                                            handleCategoryChange={handleCategoryChange}
                                        />
                                    ) : (
                                        <WalkmePopupWrapper
                                            walkmeName={WalkmeName.TRANSACTION_CLASSIFICATION}
                                            stepIndex={4}
                                        >
                                            <TransactionCategoryCell
                                                categories={categories}
                                                baseFieldId="transaction"
                                                transaction={transaction}
                                                className={styles.dropdown}
                                                handleCategoryChange={handleCategoryChange}
                                            />
                                        </WalkmePopupWrapper>
                                    )
                                ) : null}
                                <EditTransactionButton
                                    transactionId={transaction.id}
                                    transactionName={transaction.displayName}
                                />
                            </div>
                        </>
                    )}
                </div>
                {showTransactionRulePrompt ? (
                    <div className={styles.rulePromptMargin}>
                        <RulePromptWrapper
                            transaction={displayingTransactionRulePrompt}
                            onClose={handleCloseRulePrompt}
                            state={rulePromptState}
                            setState={setRulePromptState}
                        />
                    </div>
                ) : (
                    <div className={styles.dividerPadding}>
                        <Divider color="weak" />
                    </div>
                )}
                <AutoSaveTransaction
                    transaction={transaction}
                    baseFieldId="transaction."
                    blockAutoSubmit
                    handleCategoryChange={handleCategoryChange}
                />
            </>
        </Formik>
    );
}

TransactionTableCard.useUpdatingTransaction = useUpdatingTransaction;
